<template>
  <div v-if="visible" class="modal-overlay" @click.self="handleCancel">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title text-center">Select Phone Number for MFA Texts</h4>
          <button type="button" class="close" @click="handleCancel" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <p class="mb-lg">
            <strong>To keep your account secure, multi-factor authentication through text message is now
              available.</strong> Standard text message rates from your carrier might apply.
          </p>
          <p class="mb-lg">
            Please select the cell phone number that can receive texts for MFA codes, or edit an existing number to your
            cell.
          </p>

          <strong>
            <p class="text-red">{{ error }}</p>
          </strong>

          <!-- Phone Selection with inline editing -->
          <div class="phone-selector mb-lg">
            <label class="phone-option" :class="{
              'selected': selectedPhone === 'PRIMARY',
              'editing': editingPrimary
            }">
              <input type="radio" name="phonePreference" value="PRIMARY" v-model="selectedPhone"
                :disabled="!hasValidPrimaryPhone && !editingPrimary" />
              <div class="phone-content">
                <div class="phone-header">
                  <strong>Primary Phone</strong>
                  <button v-if="!editingPrimary" type="button" class="btn btn-sm btn-link edit-btn"
                    @mousedown.stop.prevent
                    @click.stop.prevent="startEditingPrimary">
                    Edit
                  </button>
                </div>
                <div v-if="editingPrimary" class="phone-input-wrapper">
                  <input id="editPrimaryPhone" v-model="editPrimaryPhone" class="form-control phone-input-inline"
                    type="text" v-mask="'(999) 999-9999'" placeholder="(555) 123-4567" @click.stop
                    @blur="stopEditingPrimary" />
                </div>
                <div v-else class="phone-display-value">
                  <small v-if="displayPrimaryPhone || hasExistingPrimaryPhone">
                    {{ displayPrimaryPhone || formatPhoneForDisplay(user.primaryPhoneNum) }}
                  </small>
                  <small v-else class="text-muted">(Not set)</small>
                </div>
              </div>
            </label>

            <label class="phone-option" :class="{
              'selected': selectedPhone === 'SECONDARY',
              'editing': editingSecondary
            }">
              <input type="radio" name="phonePreference" value="SECONDARY" v-model="selectedPhone"
                :disabled="!hasValidSecondaryPhone && !editingSecondary" />
              <div class="phone-content">
                <div class="phone-header">
                  <strong>Secondary Phone</strong>
                  <button v-if="!editingSecondary" type="button" class="btn btn-sm btn-link edit-btn"
                    @mousedown.stop.prevent
                    @click.stop.prevent="startEditingSecondary">
                    Edit
                  </button>
                </div>
                <div v-if="editingSecondary" class="phone-input-wrapper">
                  <input id="editSecondaryPhone" v-model="editSecondaryPhone" class="form-control phone-input-inline"
                    type="text" v-mask="'(999) 999-9999'" placeholder="(555) 123-4567" @click.stop
                    @blur="stopEditingSecondary" />
                </div>
                <div v-else class="phone-display-value">
                  <small v-if="displaySecondaryPhone || hasExistingSecondaryPhone">
                    {{ displaySecondaryPhone || formatPhoneForDisplay(user.secondaryPhoneNum) }}
                  </small>
                  <small v-else class="text-muted">(Not set)</small>
                </div>
              </div>
            </label>
          </div>

          <div class="alert alert-info"
            v-if="!hasValidPrimaryPhone && !hasValidSecondaryPhone && !editingPrimary && !editingSecondary">
            <em class="icon-info pr-sm"></em>
            You need at least one phone number for SMS MFA. Please add a phone number above.
          </div>
        </div>
        <div class="modal-footer modal-buttons">
          <button type="button" class="btn btn-default btn-lg" @click="handleEmailOnly">I only want to receive login codes through email</button>
          <button type="button" class="btn btn-success btn-lg" @click="handleSave" :disabled="saving || !canSave">
            {{ saving ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MfaPhonePreferenceDialog',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    user: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      selectedPhone: null,
      editPrimaryPhone: '',
      editSecondaryPhone: '',
      editingPrimary: false,
      editingSecondary: false,
      saving: false,
      error: '',
    };
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.initializeData();
      }
    },
    user: {
      handler() {
        if (this.visible) {
          this.initializeData();
        }
      },
      deep: true,
    },
    editPrimaryPhone() {
      // Don't auto-switch if user is actively editing this field
      if (this.editingPrimary) {
        return;
      }
      // Auto-select PRIMARY if it's the only valid phone
      if (this.hasValidPrimaryPhone && !this.hasValidSecondaryPhone) {
        if (!this.selectedPhone || this.selectedPhone === 'SECONDARY') {
          this.selectedPhone = 'PRIMARY';
        }
      }
      // If primary becomes invalid and secondary is valid, switch to secondary
      // But only if user is not actively editing primary
      if (!this.hasValidPrimaryPhone && this.hasValidSecondaryPhone && this.selectedPhone === 'PRIMARY') {
        this.selectedPhone = 'SECONDARY';
      }
    },
    editSecondaryPhone() {
      // Don't auto-switch if user is actively editing this field
      if (this.editingSecondary) {
        return;
      }
      // Auto-select SECONDARY if it's the only valid phone
      if (this.hasValidSecondaryPhone && !this.hasValidPrimaryPhone) {
        if (!this.selectedPhone || this.selectedPhone === 'PRIMARY') {
          this.selectedPhone = 'SECONDARY';
        }
      }
      // If secondary becomes invalid and primary is valid, switch to primary
      // But only if user is not actively editing secondary
      if (!this.hasValidSecondaryPhone && this.hasValidPrimaryPhone && this.selectedPhone === 'SECONDARY') {
        this.selectedPhone = 'PRIMARY';
      }
    },
  },
  computed: {
    hasValidPrimaryPhone() {
      const digits = this.getPhoneDigits(this.editPrimaryPhone);
      return digits && digits.length === 10;
    },
    hasValidSecondaryPhone() {
      const digits = this.getPhoneDigits(this.editSecondaryPhone);
      return digits && digits.length === 10;
    },
    displayPrimaryPhone() {
      // Show primary phone from prop or from edit field
      if (this.primaryPhone) {
        return this.primaryPhone;
      }
      if (this.hasValidPrimaryPhone) {
        return this.formatPhoneForDisplay(this.getPhoneDigits(this.editPrimaryPhone));
      }
      return null;
    },
    displaySecondaryPhone() {
      // Show secondary phone from prop or from edit field
      if (this.secondaryPhone) {
        return this.secondaryPhone;
      }
      if (this.hasValidSecondaryPhone) {
        return this.formatPhoneForDisplay(this.getPhoneDigits(this.editSecondaryPhone));
      }
      return null;
    },
    hasExistingPrimaryPhone() {
      // Check if user has an existing primary phone number
      return !!(this.user && this.user.primaryPhoneNum);
    },
    hasExistingSecondaryPhone() {
      // Check if user has an existing secondary phone number
      return !!(this.user && this.user.secondaryPhoneNum);
    },
    canSave() {
      // Must have at least one valid phone (10 digits) and select a preference
      if (!this.hasValidPrimaryPhone && !this.hasValidSecondaryPhone) {
        return false;
      }

      // Must select a preference for a valid phone
      if (this.hasValidPrimaryPhone && !this.hasValidSecondaryPhone) {
        return this.selectedPhone === 'PRIMARY';
      }
      if (!this.hasValidPrimaryPhone && this.hasValidSecondaryPhone) {
        return this.selectedPhone === 'SECONDARY';
      }
      if (this.hasValidPrimaryPhone && this.hasValidSecondaryPhone) {
        return this.selectedPhone === 'PRIMARY' || this.selectedPhone === 'SECONDARY';
      }

      return false;
    },
  },
  methods: {
    initializeData() {
      if (!this.user) return;

      // Initialize edit fields with current values
      this.editPrimaryPhone = this.formatPhoneForDisplay(this.user.primaryPhoneNum) || '';
      this.editSecondaryPhone = this.formatPhoneForDisplay(this.user.secondaryPhoneNum) || '';

      // Reset editing states - start in edit mode if no phone exists, view mode if phone exists
      this.editingPrimary = !this.hasExistingPrimaryPhone;
      this.editingSecondary = !this.hasExistingSecondaryPhone;

      // Set initial selection based on current data
      if (this.user.mfaPhonePreference) {
        this.selectedPhone = this.user.mfaPhonePreference;
        // If current preference is set but the phone is now invalid, clear it
        if (this.user.mfaPhonePreference === 'PRIMARY' && !this.hasValidPrimaryPhone) {
          this.selectedPhone = null;
        } else if (this.user.mfaPhonePreference === 'SECONDARY' && !this.hasValidSecondaryPhone) {
          this.selectedPhone = null;
        }
      } else if (this.hasValidPrimaryPhone && !this.hasValidSecondaryPhone) {
        // Auto-select PRIMARY if it's the only valid phone
        this.selectedPhone = 'PRIMARY';
      } else if (!this.hasValidPrimaryPhone && this.hasValidSecondaryPhone) {
        // Auto-select SECONDARY if it's the only valid phone
        this.selectedPhone = 'SECONDARY';
      } else if (this.hasValidPrimaryPhone && this.hasValidSecondaryPhone) {
        // Default to PRIMARY if both exist
        this.selectedPhone = 'PRIMARY';
      } else {
        // No valid phones
        this.selectedPhone = null;
      }
    },
    startEditingPrimary(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
      console.log('startEditingPrimary called', this.editingPrimary);
      this.editingPrimary = true;
      this.$nextTick(() => {
        const input = this.$el.querySelector('#editPrimaryPhone');
        if (input) {
          input.focus();
        }
      });
    },
    startEditingSecondary(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
      console.log('startEditingSecondary called', this.editingSecondary);
      this.editingSecondary = true;
      this.$nextTick(() => {
        const input = this.$el.querySelector('#editSecondaryPhone');
        if (input) {
          input.focus();
        }
      });
    },
    stopEditingPrimary() {
      // If there was an existing phone number, go back to display mode on blur
      // If adding a new number, only go back if field is empty
      if (this.hasExistingPrimaryPhone) {
        // Restore original value if user didn't make valid changes
        if (!this.hasValidPrimaryPhone) {
          this.editPrimaryPhone = this.formatPhoneForDisplay(this.user.primaryPhoneNum) || '';
        }
        this.editingPrimary = false;
      } else if (!this.editPrimaryPhone || !this.hasValidPrimaryPhone) {
        // Adding new number - only stop editing if field is empty
        // Keep editing if they're in the middle of typing
        // Only stop if field is completely empty
        if (!this.editPrimaryPhone) {
          this.editingPrimary = false;
        }
      } else {
        // Valid phone entered, can stop editing
        this.editingPrimary = false;
      }
    },
    stopEditingSecondary() {
      // If there was an existing phone number, go back to display mode on blur
      // If adding a new number, only go back if field is empty
      if (this.hasExistingSecondaryPhone) {
        // Restore original value if user didn't make valid changes
        if (!this.hasValidSecondaryPhone) {
          this.editSecondaryPhone = this.formatPhoneForDisplay(this.user.secondaryPhoneNum) || '';
        }
        this.editingSecondary = false;
      } else if (!this.editSecondaryPhone || !this.hasValidSecondaryPhone) {
        // Adding new number - only stop editing if field is empty
        // Keep editing if they're in the middle of typing
        // Only stop if field is completely empty
        if (!this.editSecondaryPhone) {
          this.editingSecondary = false;
        }
      } else {
        // Valid phone entered, can stop editing
        this.editingSecondary = false;
      }
    },
    formatPhoneForDisplay(phone) {
      if (!phone) return '';
      // Remove masking characters and format
      const digits = phone.replace(/\D/g, '');
      if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
      }
      return phone;
    },
    getPhoneDigits(phone) {
      return phone ? phone.replace(/\D/g, '') : '';
    },
    async handleSave() {
      if (!this.canSave) {
        return;
      }

      if (!this.user || !this.user.userId) {
        this.error = 'User information is missing. Please try again.';
        return;
      }

      this.saving = true;
      this.error = '';

      try {
        const primaryDigits = this.getPhoneDigits(this.editPrimaryPhone);
        const secondaryDigits = this.getPhoneDigits(this.editSecondaryPhone);

        // Validate phone numbers
        if (primaryDigits && primaryDigits.length !== 10) {
          this.error = 'Primary phone number must be 10 digits';
          this.saving = false;
          return;
        }

        if (secondaryDigits && secondaryDigits.length !== 10) {
          this.error = 'Secondary phone number must be 10 digits';
          this.saving = false;
          return;
        }

        // Build full user payload with all required fields
        // Merge phone number changes into the existing user object
        // Filter out properties that shouldn't be sent to the API
        const userPayload = {
          ...this.user,
          mfaPhonePreference: this.selectedPhone,
          primaryPhoneNum: primaryDigits || '',
          secondaryPhoneNum: secondaryDigits || '',
        };

        // Remove properties that shouldn't be sent to the API
        delete userPayload.dwollaCustomerId;
        delete userPayload.accounts;
        delete userPayload.roles;
        delete userPayload.accountType;
        delete userPayload.organizationName;
        delete userPayload.CRDnumber;
        delete userPayload.userId;

        // Remove masked or partial SSN
        // This matches the pattern in EditUserProfile.vue cleanPayload()
        // Don't send SSN if:
        // 1. It contains 'x' (masked) OR
        // 2. It's not a valid 9-digit SSN (partial/incomplete)
        if (userPayload.socialSecurityNum) {
          const ssnDigits = userPayload.socialSecurityNum.replace(/[- ]/g, '');
          // Remove if masked (contains x) or if not exactly 9 digits (incomplete/partial)
          if (ssnDigits.includes('x') || ssnDigits.includes('X') || !/^\d{9}$/.test(ssnDigits)) {
            delete userPayload.socialSecurityNum;
          }
        }

        // Use the Vuex action which handles all required fields
        await this.$store.dispatch('userCreationByAdmin/updateUserProfile', {
          user: userPayload,
          provideLogin: true,
          enableMfa: true,
        });

        // Emit success event
        this.$emit('saved', {
          mfaPhonePreference: this.selectedPhone,
          primaryPhone: primaryDigits ? this.formatPhoneForDisplay(primaryDigits) : null,
          secondaryPhone: secondaryDigits ? this.formatPhoneForDisplay(secondaryDigits) : null,
        });

        this.handleCancel();
      } catch (err) {
        this.error = err.message || 'Failed to save phone preference. Please try again.';
      } finally {
        this.saving = false;
      }
    },
    handleCancel() {
      this.$emit('cancel');
      this.error = '';
      this.initializeData();
    },
    async handleEmailOnly() {
      // Set preference to NONE and save
      if (!this.user || !this.user.userId) {
        this.error = 'User information is missing. Please try again.';
        return;
      }

      this.saving = true;
      this.error = '';

      try {
        // Build full user payload with NONE preference
        const userPayload = {
          ...this.user,
          mfaPhonePreference: 'NONE',
        };

        // Remove properties that shouldn't be sent to the API
        delete userPayload.dwollaCustomerId;
        delete userPayload.accounts;
        delete userPayload.roles;
        delete userPayload.accountType;
        delete userPayload.organizationName;
        delete userPayload.CRDnumber;
        delete userPayload.userId;

        // Remove masked or partial SSN
        if (userPayload.socialSecurityNum) {
          const ssnDigits = userPayload.socialSecurityNum.replace(/[- ]/g, '');
          if (ssnDigits.includes('x') || ssnDigits.includes('X') || !/^\d{9}$/.test(ssnDigits)) {
            delete userPayload.socialSecurityNum;
          }
        }

        // Use the Vuex action which handles all required fields
        await this.$store.dispatch('userCreationByAdmin/updateUserProfile', {
          user: userPayload,
          provideLogin: true,
          enableMfa: true,
        });

        // Emit success event
        this.$emit('saved', {
          mfaPhonePreference: 'NONE',
        });

        this.handleCancel();
      } catch (err) {
        this.error = err.message || 'Failed to save preference. Please try again.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.modal-dialog {
  max-width: 500px;
  width: 90%;
  margin: 0;
}

.modal-content {
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.modal-header {
  padding: 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
  padding: 0;
  width: 24px;
  height: 24px;
  line-height: 1;
}

.close:hover {
  color: #333;
}

.modal-body {
  padding: 20px;
}

.modal-footer {
  padding: 15px 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.phone-selector {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.phone-option {
  display: flex;
  align-items: flex-start;
  padding: 16px;
  border: 2px solid #ddd;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  gap: 12px;
}

.phone-option.editing {
  cursor: default;
}

.phone-option:hover {
  border-color: #4A90E2;
  background-color: #f8f9fa;
}

.phone-option.selected {
  border-color: #4A90E2;
  background-color: #f0f7ff;
}

.phone-option.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: #f5f5f5;
}

.phone-option.disabled:hover {
  border-color: #ddd;
  background-color: #f5f5f5;
}

.phone-option input[type="radio"] {
  margin-right: 12px;
  width: 20px;
  height: 20px;
  cursor: pointer;
}

.phone-text {
  flex: 1;
}

.phone-text strong {
  color: #333;
  font-size: 16px;
}

.phone-text small {
  color: #666;
  font-size: 13px;
}

.phone-editor {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.phone-editor .form-group {
  margin-bottom: 15px;
}

.phone-editor label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  color: #333;
}

.text-red {
  color: #a94442;
}

.mb-lg {
  margin-bottom: 20px;
}

.phone-display {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #f9f9f9;
}

.phone-value {
  font-size: 14px;
  color: #333;
  flex: 1;
}

.phone-display .btn-link {
  padding: 0;
  margin-left: 10px;
  color: #127285;
  text-decoration: none;
  font-size: 14px;
}

.phone-display .btn-link:hover {
  text-decoration: underline;
  color: #0e505e;
}

.text-center {
  text-align: center;
  width: 100%;
}

.phone-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.phone-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.edit-btn {
  padding: 0;
  margin: 0;
  color: #127285;
  text-decoration: none;
  font-size: 13px;
  font-weight: normal;
}

.edit-btn:hover {
  text-decoration: underline;
  color: #0e505e;
}

.phone-input-wrapper {
  width: 100%;
}

.phone-input-inline {
  width: 100%;
  padding: 6px 12px;
  font-size: 14px;
}

.phone-display-value {
  margin-top: 4px;
}

.phone-option input[type="radio"] {
  margin-top: 4px;
  flex-shrink: 0;
}
</style>



// WEBPACK FOOTER //
// MfaPhonePreferenceDialog.vue?e3958d76